import { useState } from 'react';
import { reportStepsAPI } from '../../services/api';
import { useReportState } from './useReportState';
import { getTaskSelection, saveTaskSelection } from '../../utils/taskIdValidator';
import { useAuth } from '../../context/AuthContext';
import i18n from '../../i18n';

export const useInitReport = () => {
  const { user } = useAuth();
  const {
    state,
    setTaskId,
    setCurrentStep,
    setIsLoading,
    setError,
    setTaskStatus
  } = useReportState();
  
  // State for insufficient balance error
  const [insufficientBalanceError, setInsufficientBalanceError] = useState<{
    balance: number;
    requiredBalance: number;
  } | null>(null);

  // Refresh task status
  const refreshTaskStatus = async () => {
    const { taskId: currentTaskId } = getTaskSelection();
    if (!currentTaskId) return;
    
    try {
      const response = await reportStepsAPI.getTaskStatus(currentTaskId);
      setTaskStatus(response.data);
      
      if (response.data.step === 'completed') {
        setCurrentStep('completed');
      } else if (response.data.error) {
        setError(response.data.error);
        setCurrentStep('error');
      }
    } catch (err: any) {
      console.error('Ошибка получения статуса задачи:', err);
    }
  };

  // Initialize task
  const initTask = async (reportType: string, isPro: boolean = false) => {
    console.log('[useInitReport] initTask - начало инициализации задачи, тип отчета:', reportType, 'isPro:', isPro);
    try {
      setIsLoading(true);
      setError(null);
      setInsufficientBalanceError(null);

      // Create new task
      console.log('[useInitReport] Вызываем API для инициализации задачи с типом:', reportType, 'isPro:', isPro);
      const response = await reportStepsAPI.initReportTask(reportType, isPro);
      console.log('[useInitReport] Ответ API инициализации задачи:', response.data);
      
      const newTaskId = response.data.task_id;
      console.log('[useInitReport] Получен новый taskId:', newTaskId);
      setTaskId(newTaskId);
      
      // Save taskId to localStorage
      saveTaskSelection(newTaskId);
      console.log('[useInitReport] Сохранен новый taskId в localStorage:', newTaskId);
      
      // Update task status
      console.log('[useInitReport] Обновляем статус новой задачи');
      await refreshTaskStatus();
      
      setCurrentStep('upload');
      console.log('[useInitReport] Установлен текущий шаг для новой задачи: upload');
    } catch (err: any) {
      console.error('[useInitReport] Ошибка при инициализации задачи:', err);
      
      // Check if this is a payment required error (402)
      if (err.response?.status === 402) {
        console.log('[useInitReport] Ошибка 402: Недостаточно средств на балансе');

        // Get the report generation cost from the backend config
        try {
          const configResponse = await reportStepsAPI.getReportGenerationCost();
          const requiredBalance = configResponse.data.report_generation_cost;
          const currentBalance = user?.balance || 0;

          // Set insufficient balance error state
          setInsufficientBalanceError({
            balance: currentBalance,
            requiredBalance: requiredBalance
          });

          setCurrentStep('insufficient_balance');
          console.log('[useInitReport] Установлен статус недостаточного баланса');
        } catch (configErr) {
          console.error('[useInitReport] Ошибка при получении стоимости генерации отчета:', configErr);
          setInsufficientBalanceError({
            balance: user?.balance || 0,
            requiredBalance: 100 // Default value if we can't get the actual cost
          });
          setCurrentStep('insufficient_balance');
        }
      } else if (err.response?.status === 409) {
        // Handle conflict error (active task exists)
        console.log('[useInitReport] Ошибка 409: У пользователя уже есть активная задача');
        const detail = err.response?.data?.detail;
        const errorMessage = typeof detail === 'object' ? detail.message : detail;
        setError(errorMessage || i18n.t('common:error'));
        setCurrentStep('error');
        console.error('[useInitReport] Активная задача:', detail?.active_task_id);
      } else {
        // Handle other errors
        const detail = err.response?.data?.detail;
        const errorMessage = typeof detail === 'object' ? detail.message : detail;
        setError(errorMessage || i18n.t('common:error'));
        setCurrentStep('error');
        console.error('[useInitReport] Установлен статус ошибки:', errorMessage || i18n.t('common:error'));
      }
    } finally {
      setIsLoading(false);
      console.log('[useInitReport] initTask - завершение инициализации задачи');
    }
  };

  return {
    initTask,
    refreshTaskStatus,
    isLoading: state.isLoading,
    error: state.error,
    currentStep: state.currentStep,
    insufficientBalanceError
  };
};
